import { useRef, useState } from "react";
import { Eyebrow } from "./Blocks";
import { institutionCases } from "../data/content";
import { groupVisitImage, groupVisitRecords } from "../data/groupVisits";
import { imageSize } from "../data/imageSizes";

function InstitutionName({ name }) {
  const match = name.match(/^(.*?)(어린이집|유치원)$/);
  if (!match) return name;
  return <>{match[1]}<wbr />{match[2]}</>;
}

export function OnSiteCarousel() {
  const track = useRef(null);
  const [active, setActive] = useState(0);
  const go = (index) => {
    const next = Math.max(0, Math.min(institutionCases.length - 1, index));
    const el = track.current;
    const target = el?.children[next];
    if (el && target) el.scrollTo({ left: target.offsetLeft - el.offsetLeft, behavior: "smooth" });
    setActive(next);
  };
  const sync = () => {
    const el = track.current;
    if (!el) return;
    const children = [...el.children];
    setActive(children.reduce((best, child, i) => Math.abs((child.offsetLeft - el.offsetLeft) - el.scrollLeft) < Math.abs((children[best].offsetLeft - el.offsetLeft) - el.scrollLeft) ? i : best, 0));
  };

  return <section className="onsite-cases" id="cases">
    <div className="onsite-cases-head"><div><Eyebrow>ON-SITE CASES</Eyebrow><h2>지역과 공간을 넘어,<br />아이들 곁으로 찾아갑니다</h2></div><div className="onsite-summary"><p>공공 공연장부터 육아종합지원센터까지, 관객과 공간에 맞춰 완성한 찾아가는 클래식 공연의 기록입니다.</p><strong>7개 지역 · 2025–2026</strong></div></div>
    <div className="onsite-track" ref={track} onScroll={sync} onKeyDown={(event) => { if (event.key === "ArrowLeft") go(active - 1); if (event.key === "ArrowRight") go(active + 1); }} tabIndex="0" aria-label="찾아가는 공연 사례 캐러셀">
      {institutionCases.map((item, i) => <article className="onsite-card" key={`${item.region}-${item.date}`}>
        <figure><img src={item.image} alt={`${item.region} ${item.institution} ${item.title} 공연 현장`} {...imageSize(item.image)} loading="lazy" decoding="async" /></figure>
        <div className="onsite-meta"><span>{item.date} · {item.region}</span><span>{String(i + 1).padStart(2, "0")} / {String(institutionCases.length).padStart(2, "0")}</span></div>
        <h3>{item.title}</h3>
        <dl><div><dt>주최·주관</dt><dd>{item.institution}</dd></div><div><dt>장소</dt><dd>{item.venue}</dd></div><div><dt>관객</dt><dd>{item.audience}</dd></div></dl>
      </article>)}
    </div>
    <div className="onsite-controls"><div className="onsite-progress" aria-hidden="true"><i style={{ width: `${((active + 1) / institutionCases.length) * 100}%` }} /></div><span>{String(active + 1).padStart(2, "0")} <em>/ {String(institutionCases.length).padStart(2, "0")}</em></span><button type="button" onClick={() => go(active - 1)} disabled={active === 0} aria-label="이전 공연 사례">←</button><button type="button" onClick={() => go(active + 1)} disabled={active === institutionCases.length - 1} aria-label="다음 공연 사례">→</button></div>
  </section>;
}

function GroupVisitCard({ record }) {
  const [activePhoto, setActivePhoto] = useState(0);
  const image = record.images[activePhoto];
  // 사진마다 기관·지역·회차를 담은 설명을 붙여 이미지 검색에 노출되도록 합니다.
  const photoAlt = (order) => `${record.institutions.join("·")} 단체관람 현장 ${order} · ${record.region} ${record.date}`;

  return <div className="visit-showcase">
    <figure className="visit-main-photo"><img src={groupVisitImage(image)} alt={photoAlt(activePhoto + 1)} {...imageSize(groupVisitImage(image))} style={{ objectPosition: image === 29 ? "center top" : "center" }} loading="lazy" decoding="async" /><figcaption>{record.images.length > 1 ? `PHOTO ${activePhoto + 1} / ${record.images.length}` : "GROUP VISIT"}</figcaption></figure>
    <article className="visit-record-copy"><p className="visit-date">{record.date} · {record.region}</p><h3>{record.institutions.map((name) => <span key={name}><InstitutionName name={name} /></span>)}</h3><dl><div><dt>관람 인원</dt><dd>{record.attendees.toLocaleString()}명</dd></div><div><dt>운영 형태</dt><dd>{record.note || "어린이집 단체관람"}</dd></div></dl>{record.images.length > 1 && <div className="visit-thumbs" aria-label="현재 관람 회차 사진 선택">{record.images.map((photo, i) => <button type="button" className={i === activePhoto ? "active" : ""} onClick={() => setActivePhoto(i)} key={photo} aria-pressed={i === activePhoto}><img src={groupVisitImage(photo)} alt={`${photoAlt(i + 1)} 보기`} {...imageSize(groupVisitImage(photo))} loading="lazy" decoding="async" /></button>)}</div>}</article>
  </div>;
}

export function GroupVisitArchive() {
  const track = useRef(null);
  const [activeRecord, setActiveRecord] = useState(0);
  const go = (index) => {
    const next = (index + groupVisitRecords.length) % groupVisitRecords.length;
    const el = track.current;
    const target = el?.children[next];
    if (el && target) el.scrollTo({ left: target.offsetLeft - el.offsetLeft, behavior: "smooth" });
    setActiveRecord(next);
  };
  const sync = () => {
    const el = track.current;
    if (!el) return;
    const children = [...el.children];
    setActiveRecord(children.reduce((best, child, i) => Math.abs((child.offsetLeft - el.offsetLeft) - el.scrollLeft) < Math.abs((children[best].offsetLeft - el.offsetLeft) - el.scrollLeft) ? i : best, 0));
  };

  return <section className="visit-archive" id="records">
    <div className="visit-archive-head"><div><Eyebrow>GROUP VISIT ARCHIVE</Eyebrow><h2>아이들과 함께한<br />단체관람의 기록</h2></div><div><p>동탄 공연장을 찾은 어린이집의 관람 현장을 회차별 사진으로 소개합니다.</p></div></div>
    <div className="visit-track" ref={track} onScroll={sync} onKeyDown={(event) => { if (event.key === "ArrowLeft") go(activeRecord - 1); if (event.key === "ArrowRight") go(activeRecord + 1); }} tabIndex="0" aria-label="어린이집·유치원 단체관람 실적 캐러셀">{groupVisitRecords.map((record) => <GroupVisitCard record={record} key={`${record.date}-${record.institutions.join("-")}`} />)}</div>
    <div className="visit-controls"><div className="visit-progress" aria-hidden="true"><i style={{ width: `${((activeRecord + 1) / groupVisitRecords.length) * 100}%` }} /></div><small>좌우로 넘겨보기</small><button type="button" onClick={() => go(activeRecord - 1)} aria-label="이전 단체관람 실적">←</button><button type="button" onClick={() => go(activeRecord + 1)} aria-label="다음 단체관람 실적">→</button></div>
  </section>;
}
